<template>
  <div
    class="game-control-bar bottom-0 left-0 right-0"
    :class="{ 'game-control-bar--hidden': !chrome.visible.value }"
  >
    <!-- Restart / Play keep the centre of the bar; the toggles sit absolutely on
         the right so the centre stays put whatever the toggle count is. -->
    <div
      v-if="mode === 'full'"
      class="control-bar-center"
    >
      <button
        class="control-btn control-btn-restart"
        :disabled="actionsDisabled"
        @click="$emit('restart')"
      >
        <ArrowPathIcon class="w-5 h-5" />
        Restart
      </button>
      <button
        class="control-btn control-btn-primary"
        :disabled="actionsDisabled"
        @click="$emit('toggle-play')"
      >
        <PauseIcon
          v-if="isPlaying"
          class="w-5 h-5"
        />
        <PlayIcon
          v-else-if="!isPreparingStart"
          class="w-5 h-5"
        />
        {{ isPreparingStart ? 'Preparing…' : (isPlaying ? 'Pause' : 'Play') }}
      </button>
    </div>

    <div class="control-bar-right">
      <template v-if="showLoopControls">
        <UTooltip
          :text="loopPreRollTooltip"
          :delay-duration="0"
        >
          <ToolbarIconToggle
            :active="gameEngine.loopPreRollEveryPass.value"
            :aria-label="gameEngine.loopPreRollEveryPass.value ? 'Lead-in on every loop pass' : 'Lead-in on first play only'"
            :aria-pressed="gameEngine.loopPreRollEveryPass.value"
            @click="gameEngine.loopPreRollEveryPass.value = !gameEngine.loopPreRollEveryPass.value"
          >
            <CountInIcon class="count-in-icon" />
            <!-- Badge says how often the lead-in applies: the loop glyph for
                 every pass, a bare "1" for once. One or the other always renders. -->
            <template #badge>
              <ArrowPathRoundedSquareIcon
                v-if="gameEngine.loopPreRollEveryPass.value"
                class="loop-mode-badge"
              />
              <span
                v-else
                class="loop-mode-badge loop-mode-badge-text"
              >1</span>
            </template>
          </ToolbarIconToggle>
        </UTooltip>

        <UTooltip
          :text="loopToggleTooltip"
          :delay-duration="0"
        >
          <ToolbarIconToggle
            :active="gameEngine.loopEnabled.value"
            :disabled="!gameEngine.hasLoopSelection.value && !isLoopSelectionReady"
            :aria-label="loopToggleTooltip"
            :aria-pressed="gameEngine.loopEnabled.value"
            @click="handleLoopToggle"
          >
            <ArrowPathRoundedSquareIcon class="w-5 h-5" />
          </ToolbarIconToggle>
        </UTooltip>
      </template>

      <UTooltip
        v-if="showLoopSectionButton"
        :text="loopSectionTooltip"
        :delay-duration="0"
      >
        <ToolbarIconToggle
          :active="!showLoopControls && gameEngine.loopEnabled.value"
          :disabled="!isLoopSelectionReady"
          aria-label="Loop section"
          @click="showSectionSelector = true"
        >
          <QueueListIcon class="w-5 h-5" />
        </ToolbarIconToggle>
      </UTooltip>

      <UTooltip
        text="Settings"
        :delay-duration="0"
      >
        <ToolbarIconToggle
          class="settings-toggle-button"
          :active="showSettingsPane"
          aria-label="Settings"
          :aria-expanded="showSettingsPane"
          @click="showSettingsPane = !showSettingsPane"
        >
          <AdjustmentsHorizontalIcon class="w-5 h-5" />
        </ToolbarIconToggle>
      </UTooltip>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { PlayIcon, PauseIcon } from '@heroicons/vue/24/solid';
import {
  ArrowPathIcon,
  ArrowPathRoundedSquareIcon,
  QueueListIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/vue/24/outline';
import { useInjectGameEngine } from '@/composables/useGameEngine';
import { useInjectSheetMusic } from '@/composables/useSheetMusic';
import { useUserLoopSectionStorage } from '@/composables/useUserLoopSectionStorage';
import { useGameChromeVisibility } from '@/composables/useGameChromeVisibility';
import CountInIcon from '@/components/CountInIcon.vue';
import ToolbarIconToggle from '@/components/shared/ToolbarIconToggle.vue';
import type { Id } from '../../convex/_generated/dataModel';
import type { LoopSource } from '@/types/classroom';

const props = withDefaults(defineProps<{
  /** `'full'` shows Restart/Play plus the loop controls; `'settings-only'` drops both. */
  mode?: 'full' | 'settings-only';
  isPlaying: boolean;
  /** Disables Restart and Play/Pause. */
  actionsDisabled?: boolean;
  /** When true, Play swaps its label to "Preparing…" and hides the icon. */
  isPreparingStart?: boolean;
  /** Required for the loop buttons to render — without a piece, there's nothing to loop within. */
  musicalPieceId?: Id<'musicalPieces'>;
  /** Show the Loop section icon in `settings-only` mode for read-only inspection (classroom participants). */
  loopViewOnly?: boolean;
}>(), {
  mode: 'full',
  actionsDisabled: false,
  isPreparingStart: false,
  loopViewOnly: false,
});

const emit = defineEmits<{
  (e: 'restart'): void;
  (e: 'toggle-play'): void;
  (e: 'loop-update', data: { startMeasure: number | null; endMeasure: number | null; source: LoopSource }): void;
}>();

const showSectionSelector = defineModel<boolean>('showSectionSelector', { default: false });
const showSettingsPane = defineModel<boolean>('showSettingsPane', { default: false });

const gameEngine = useInjectGameEngine();
const sheetMusic = useInjectSheetMusic();
const chrome = useGameChromeVisibility();
const { userLoopSection } = useUserLoopSectionStorage(computed(() => props.musicalPieceId));

// Writing to the loop is the host's / solo player's job; participants get the
// read-only section view and nothing else.
const showLoopControls = computed(() => !!props.musicalPieceId && props.mode === 'full');
const showLoopSectionButton = computed(
  () => !!props.musicalPieceId && (props.mode === 'full' || props.loopViewOnly)
);

const isLoopSelectionReady = computed(() => {
  if (!sheetMusic.isLoaded.value) return false;
  if (sheetMusic.measureTimingMap.value.length === 0) return false;
  if (gameEngine.currentGameNotes.value.length === 0) return false;
  return true;
});

// A loop saved on an earlier visit isn't in the engine until the selector applies it, so
// the toggle acts on storage too. A re-uploaded score can leave that range dangling, and
// enabling one the engine can't time would strand the toggle on a loop that never wraps.
const savedLoop = computed(() => {
  const saved = userLoopSection.value;
  if (!saved) return null;
  return saved.endMeasureIndex < sheetMusic.measureTimingMap.value.length ? saved : null;
});

const loopPreRollTooltip = computed(() =>
  gameEngine.loopPreRollEveryPass.value
    ? 'Lead-in measure replays on every loop pass. Click for lead-in on the first play only.'
    : 'Lead-in measure plays only when you press Play. Click to replay it on every loop pass.'
);

const TIMING_PENDING_TOOLTIP = 'Loop unavailable until timing is ready';

const loopToggleTooltip = computed(() => {
  if (!gameEngine.hasLoopSelection.value) {
    if (!isLoopSelectionReady.value) return TIMING_PENDING_TOOLTIP;
    if (!savedLoop.value) return 'Pick a loop section';
  }
  return gameEngine.loopEnabled.value ? 'Looping on' : 'Looping off';
});

const loopSectionTooltip = computed(() => {
  if (!isLoopSelectionReady.value) return TIMING_PENDING_TOOLTIP;
  if (props.loopViewOnly) return gameEngine.loopEnabled.value ? 'Loop enabled (host)' : 'View piece sections';
  return 'Select loop section';
});

// The room mirrors the loop, so a local toggle has to travel up to the classroom
// view — without the broadcast the next room snapshot would just undo it.
const handleLoopToggle = () => {
  if (gameEngine.hasLoopSelection.value) {
    gameEngine.setLoopEnabled(!gameEngine.loopEnabled.value);
  } else if (savedLoop.value) {
    const { startMeasureIndex, endMeasureIndex } = savedLoop.value;
    gameEngine.setLoopSection(startMeasureIndex, endMeasureIndex, true, 'custom');
  } else {
    // Nothing picked or saved: no loop to switch on, so the toggle opens the picker.
    showSectionSelector.value = true;
    return;
  }
  emit('loop-update', {
    startMeasure: gameEngine.loopStartMeasure.value,
    endMeasure: gameEngine.loopEndMeasure.value,
    source: gameEngine.loopSource.value,
  });
};
</script>

<style scoped>
.game-control-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  /* Holds the bar's height when the centre group is absent (participants), so
     the absolutely-placed toggles never collapse it. */
  min-height: 66px;
  padding: 12px 16px;
  /* Floats over the full-bleed sheet music (see GameArea.vue) instead of
     sitting in flow beneath it. The scrim is built from the theme's own
     --color-secondary — see GameAreaStatusBar.vue for the same treatment —
     so text stays the normal theme-aware --color-text-* colors. */
  position: absolute;
  z-index: 20;
  background: linear-gradient(
    to top,
    color-mix(in srgb, var(--color-secondary) 92%, transparent),
    color-mix(in srgb, var(--color-secondary) 55%, transparent) 60%,
    transparent
  );
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.game-control-bar--hidden {
  opacity: 0;
  transform: translateY(100%);
  pointer-events: none;
}

.control-bar-center {
  display: flex;
  align-items: center;
  gap: 12px;
}

.control-bar-right {
  position: absolute;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Four digits in a row need more width than the square 20px toolbar icons. */
.count-in-icon {
  width: 31px;
}

/* Subscript glyph on the loop lead-in disc, sized and tinted like the chip on
   the preview toggle so the two read as the same class of annotation. */
.loop-mode-badge {
  width: 13px;
  height: 13px;
  padding: 1.5px;
  border-radius: 999px;
  background: var(--color-text-primary);
  color: var(--color-secondary);
  box-shadow: 0 0 0 1.5px var(--color-secondary);
}

/* The "once" badge is a digit, so it needs the centring and type treatment the
   heroicon variant gets for free. */
.loop-mode-badge-text {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
}

/* Wide pill action buttons (Restart / Play). The round icon toggles
   (loop, settings) are <ToolbarIconToggle>. */
.control-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.1s;
  border: none;
}

.control-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.control-btn:not(:disabled):active {
  transform: scale(0.97);
}

.control-btn-primary {
  background-color: var(--color-primary);
  color: var(--color-text-primary);
  min-width: 130px;
  justify-content: center;
}

.control-btn-primary:not(:disabled):hover {
  opacity: 0.9;
}

.control-btn-restart {
  /* --color-surface-overlay is the theme's own "chip on top of an image"
     tint — black-tinted in light mode, white-tinted in dark mode — rather
     than a fixed white overlay that vanishes on a light-mode scrim. */
  background-color: var(--color-surface-overlay);
  color: var(--color-text-primary);
}

.control-btn-restart:not(:disabled):hover {
  background-color: color-mix(in srgb, var(--color-surface-overlay) 70%, var(--color-text-primary) 30%);
}
</style>
